import { FC } from 'react';

import {
    Card,
    Divider,
    FullPageContainer,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';

import { CampaignsControls } from 'components/CampaignsDashboard/components/CampaignsControls';
import { CampaignsMobileList } from 'components/CampaignsDashboard/components/CampaignsTable/CampaignsMobileList';
import { CampaignsNotFoundByFilters } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignsNotFoundByFilters';
import { CampaignsTableHeader } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignsTableHeader';
import { CampaignsTableRow } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignsTableRow';
import { CampaignsTableSkeleton } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignsTableSkeleton';
import { NoCampaignsOnTab } from 'components/CampaignsDashboard/components/CampaignsTable/components/NoCampaignsOnTab';

import { useSelector } from 'store/utils';

export const CampaignsTable: FC = () => {
    const { isMobile } = useBreakpoint();
    const campaigns = useSelector((state) => state.campaignsDashboard.campaigns);
    const isCampaignsLoading = useSelector((state) => state.campaignsDashboard.isCampaignsLoading);
    const appliedCampaignsFiltersTotalCount = useSelector(
        (state) => state.campaignsFilters.appliedCampaignsFiltersTotalCount
    );
    const campaignsSearchValue = useSelector((state) => state.campaignsFilters.campaignsSearchValue);

    const isFiltersApplied = appliedCampaignsFiltersTotalCount > 0 || campaignsSearchValue !== '';
    const isCampaignsExist = campaigns.size > 0;
    const isNoCampaigns = !isCampaignsLoading && !isCampaignsExist && !isFiltersApplied;
    const isCampaignsNotFoundByFilters = !isCampaignsLoading && !isCampaignsExist && isFiltersApplied;
    const showCampaigns = !isCampaignsLoading && isCampaignsExist;

    if (isMobile) {
        return (
            <>
                <CampaignsMobileList />
                {isCampaignsNotFoundByFilters && <CampaignsNotFoundByFilters />}
                {isNoCampaigns && <NoCampaignsOnTab />}
            </>
        );
    }

    return (
        <>
            <FullPageContainer>
                {({ paddingLeft, paddingRight }) => (
                    <Card stretched>
                        <Table
                            stickyHeader
                            divider={isCampaignsExist}
                            dividerTop={false}
                            paddingLeft={paddingLeft}
                            paddingRight={paddingRight}
                            cellsGap={24}
                            data-qa="campaigns-table"
                            topPanel={
                                <>
                                    <VSpacing default={24} />
                                    <CampaignsControls />
                                    <VSpacing default={24} />
                                    <div
                                        style={{
                                            marginLeft: -paddingLeft,
                                            marginRight: -paddingRight,
                                            visibility: isCampaignsExist ? 'visible' : 'hidden',
                                        }}
                                    >
                                        <Divider />
                                    </div>
                                </>
                            }
                            bottomPanel={
                                <>
                                    {isCampaignsExist && !isCampaignsLoading && (
                                        <div style={{ marginLeft: -paddingLeft, marginRight: -paddingRight }}>
                                            <Divider />
                                        </div>
                                    )}
                                </>
                            }
                        >
                            {isCampaignsLoading || isCampaignsExist ? (
                                <CampaignsTableHeader />
                            ) : (
                                <TableHeader>
                                    <TableRow>
                                        <TableCell m={12} />
                                    </TableRow>
                                </TableHeader>
                            )}
                            {isCampaignsLoading && <CampaignsTableSkeleton />}
                            {showCampaigns && (
                                <TableBody>
                                    {[...campaigns.values()].map((campaign) => {
                                        return (
                                            <TableRow
                                                key={`table-row-${campaign.id}`}
                                                data-qa={`campaign-row-${campaign.id}`}
                                                verticalAlign="middle"
                                            >
                                                <CampaignsTableRow key={campaign.id} campaign={campaign} />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            )}
                        </Table>
                    </Card>
                )}
            </FullPageContainer>
            {isCampaignsNotFoundByFilters && <CampaignsNotFoundByFilters />}
            {isNoCampaigns && <NoCampaignsOnTab />}
        </>
    );
};
